import React, { useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { getContrastColor, COLOR_SUCCESS, COLOR_WARNING, COLOR_PRIMARY, COLOR_DANGER, COLOR_SECONDARY } from '@shared/colorUtils';
import { useNavigate } from 'react-router-dom';
import { api } from '@shared/api';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

const Calendar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  const { isLoading: loading } = useQuery({
    queryKey: ['rental-calendar-events'],
    queryFn: async () => {
      const response = await api.get('rentals/api/rentals/');
      const calendarEvents = response.map(rental => {
        const status = rental.rental_status;
        let color = '#6c757d';
        switch (status) {
          case 'active': color = '#198754'; break;
          case 'inactive': color = '#ffc107'; break;
          case 'completed': color = '#0d6efd'; break;
          case 'expired': color = '#dc3545'; break;
          case 'canceled': color = '#6c757d'; break;
        }
        return {
          id: rental.id,
          title: `${t('rentals.rental')} #${rental.id} - ${rental.customer_name}`,
          start: rental.date_from, end: rental.date_to, color,
          extendedProps: { rental, status, customer: rental.customer_name,
            reason: rental.rental_reason_name, issuedBy: rental.issued_by_details.display_name }
        };
      });
      setEvents(calendarEvents);
      return calendarEvents;
    },
    onError: () => setError(t('calendar.errorFetching')),
  });

  const handleEventClick = (clickInfo) => {
    const rental = clickInfo.event.extendedProps.rental;
    // Navigate to rental detail or show modal
    navigate(`/rentals/rental/${rental.id}`);
  };

  const renderEventContent = (eventInfo) => {
    return (
      <div className="fc-event-main-frame">
        <div className="fc-event-title-container">
          <div className="fc-event-title fc-sticky">
            {eventInfo.event.title}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
          <div className="mt-2">{t('common.loading')}</div>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">{t('calendar.rentalAvailability')}</h4>
          <div className="d-flex gap-2">
            <span className="badge" style={{ backgroundColor: COLOR_SUCCESS, color: getContrastColor(COLOR_SUCCESS) }}>{t('rentals.statuses.active')}</span>
            <span className="badge" style={{ backgroundColor: COLOR_WARNING, color: getContrastColor(COLOR_WARNING) }}>{t('rentals.statuses.inactive')}</span>
            <span className="badge" style={{ backgroundColor: COLOR_PRIMARY, color: getContrastColor(COLOR_PRIMARY) }}>{t('rentals.statuses.completed')}</span>
            <span className="badge" style={{ backgroundColor: COLOR_DANGER, color: getContrastColor(COLOR_DANGER) }}>{t('rentals.statuses.expired')}</span>
            <span className="badge" style={{ backgroundColor: COLOR_SECONDARY, color: getContrastColor(COLOR_SECONDARY) }}>{t('rentals.statuses.canceled')}</span>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          initialView="dayGridMonth"
          editable={false}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          events={events}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
          height="auto"
          locale={i18n.language}
          buttonText={{
            today: t('calendar.today'),
            month: t('calendar.month'),
            week: t('calendar.week'),
            day: t('calendar.day'),
            list: t('calendar.list')
          }}
          moreLinkText={count => `+${count} ${t('calendar.more')}`}
          noEventsText={t('calendar.noEvents')}
        />
      </Card.Body>
    </Card>
  );
};

export default Calendar;
